import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CreditCard, Layers, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { API } from '../config/api';
import apiClient from '../services/api';

// Costes por modo cuando aún no ha llegado /tutor/credits-info (mismos valores
// por defecto que el backend: texto 5, audio +2, voz +1). Solo para pintar.
export const FALLBACK_MODE_COSTS = {
  text_only: 5,
  text_with_audio: 7,
  voice_with_text: 6,
  voice_only: 8,
};

// Sesión de examen en curso, si la pantalla del examen la deja anotada
// (active_exam_session_id). El backend detecta por su cuenta simulacros y
// exámenes oficiales abiertos; este dato solo amplía la detección a tests sin reloj.
export const getActiveExamSessionId = () => {
  try {
    return (
      sessionStorage.getItem('active_exam_session_id')
      || localStorage.getItem('active_exam_session_id')
      || null
    );
  } catch {
    return null;
  }
};

/**
 * Única fuente de saldo, precios y acceso del tutor: GET /tutor/credits-info.
 * El saldo es users.credits (el mismo que pintan el Dashboard y /credits);
 * el precio por modo es exactamente el que cobra cada endpoint del tutor.
 */
export const useTutorCredits = () => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false); // `loading` solo en la primera carga: los refetch no parpadean

  const fetchCredits = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setCredits(null);
      setLoading(false);
      return null;
    }
    if (!loadedRef.current) setLoading(true);
    try {
      const res = await apiClient.get(`${API}/tutor/credits-info`);
      const d = res.data || {};
      const info = {
        balance: d.user_credits ?? 0,
        is_unlimited: Boolean(d.has_unlimited),
        // Backend antiguo (sin has_access): no ocultar el widget; el servidor ya responde 403 si toca
        has_access: typeof d.has_access === 'boolean' ? d.has_access : true,
        reason: d.reason || null,
        trial_expired: Boolean(d.trial_expired),
        plan_name: d.plan_name || null,
        voice_allowed: Boolean(d.voice_allowed),
        daily_cap: d.daily_cap ?? null,
        remaining_today: d.remaining_today ?? null,
        message_cost: d.text_message_cost ?? d.message_cost ?? FALLBACK_MODE_COSTS.text_only,
        costs: Object.fromEntries(
          Object.entries(d.modes || {}).map(([id, m]) => [id, m?.cost_per_message]),
        ),
      };
      setCredits(info);
      // El Dashboard y otros paneles pueden refrescar su cifra sin volver a pedirla
      try {
        window.dispatchEvent(new CustomEvent('credits:updated', { detail: { credits: info.balance } }));
      } catch {
        /* entornos sin CustomEvent */
      }
      return info;
    } catch {
      return null;
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  return { credits, loading, refetch: fetchCredits, setCredits };
};

/**
 * Modal honesto de créditos del tutor.
 * - Con pagos activos (GET /system/status): paquetes reales de GET /credits/packages
 *   y compra por POST /credits/purchase (Stripe) con redirección al checkout.
 * - Sin pagos: no vende nada; explica la situación y ofrece dos salidas de estudio gratuitas.
 */
export const CreditUpsellModal = ({
  isOpen,
  onClose,
  currentBalance = 0,
  requiredCredits = 0,
  message = '',
}) => {
  const navigate = useNavigate();
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const st = await apiClient.get(`${API}/system/status`);
        const enabled = Boolean(st.data?.payments_enabled);
        if (cancelled) return;
        setPaymentsEnabled(enabled);
        if (!enabled) return;
        const pk = await apiClient.get(`${API}/credits/packages`);
        const list = (pk.data?.packages || []).filter((p) => p.active !== false && !p.b2b_only);
        if (cancelled) return;
        setPackages(list);
        setSelectedPackage(list.find((p) => p.popular)?.id || list[0]?.id || null);
      } catch {
        if (!cancelled) setPaymentsEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    setPurchasing(true);
    try {
      const res = await apiClient.post(`${API}/credits/purchase`, { package_id: selectedPackage });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.error('No se pudo abrir el pago. Inténtalo de nuevo.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo iniciar la compra');
    } finally {
      setPurchasing(false);
    }
  };

  const irA = (path) => {
    if (onClose) onClose();
    navigate(path);
  };

  if (!isOpen) return null;

  const faltan = Math.max(0, requiredCredits - currentBalance);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="credit-modal-title"
      data-testid="credit-upsell-modal"
    >
      <div className="bg-white rounded-2xl max-w-xl w-full overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h2 id="credit-modal-title" className="text-xl font-bold">
                  {requiredCredits > 0 ? 'Te faltan créditos para este mensaje' : 'Créditos del Tutor IA'}
                </h2>
                <p className="text-indigo-100 text-sm">
                  {message
                    || (requiredCredits > 0
                      ? `Este mensaje cuesta ${requiredCredits} créditos y tienes ${currentBalance}.`
                      : `Tienes ${currentBalance} créditos.`)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 flex items-center gap-6">
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-indigo-100">Tu saldo</p>
              <p className="text-2xl font-bold">{currentBalance}</p>
            </div>
            {faltan > 0 && (
              <div className="bg-white/10 rounded-lg px-4 py-2">
                <p className="text-xs text-indigo-100">Te faltan</p>
                <p className="text-2xl font-bold">{faltan}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {paymentsEnabled ? (
            <>
              <p className="text-slate-600 text-sm">Elige un paquete para seguir usando el Tutor IA:</p>
              {packages.length === 0 ? (
                <p className="text-sm text-slate-500">Cargando paquetes…</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg.id)}
                      aria-pressed={selectedPackage === pkg.id}
                      className={`rounded-xl p-4 text-left transition-all border-2 ${
                        selectedPackage === pkg.id
                          ? 'border-indigo-500 bg-indigo-50 shadow'
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      <p className="font-bold text-slate-900">{pkg.name}</p>
                      <p className="text-2xl font-black text-indigo-600 mt-1">{pkg.price_eur} €</p>
                      <p className="text-xs text-slate-500">{Number(pkg.credits || 0).toLocaleString()} créditos</p>
                      {pkg.discount && (
                        <span className="text-xs text-emerald-600 font-medium">{pkg.discount} de ahorro</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Ahora no
                </Button>
                <Button
                  onClick={handlePurchase}
                  disabled={purchasing || !selectedPackage}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  {purchasing ? 'Abriendo el pago…' : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Comprar créditos
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-slate-700 text-sm">
                Ahora mismo no hay recarga de créditos disponible. Los tests, los simulacros, el repaso de fallos
                y las flashcards no gastan créditos: puedes seguir estudiando sin límite.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={() => irA('/practice-failed')}
                  className="bg-indigo-600 hover:bg-indigo-700 justify-start"
                  data-testid="salida-falladas"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Repasar mis fallos
                </Button>
                <Button
                  onClick={() => irA('/flashcards')}
                  variant="outline"
                  className="justify-start"
                  data-testid="salida-flashcards"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Flashcards de hoy
                </Button>
              </div>
              <Button variant="ghost" onClick={onClose} className="w-full text-slate-500">
                Cerrar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditUpsellModal;
